import React, { useState } from "react";
import "./AddNewEmployees.css";
import { API_URL } from "./API";

const AddNewEmployees = ({ onBack, onAddEmployee, onProfileImageUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    dob: '',
    dateOfJoining: '', 
    panNumber: '',
    aadharNumber: '',
    pfNumber: '',
    esiNumber: '',
    uanNumber: '',
    bankDetails: {
      accountName: '',
      accountNumber: '',
      ifscCode: '',
      branch: ''
    }
  });

  const [files, setFiles] = useState({
    profile_image: null,
    pan_image: null,
    aadhar_image: null,
    bank_image: null
  });

  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const formatAadharNumber = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const limited = cleaned.slice(0, 12);
    const formatted = limited.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted;
  };


  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return null;
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }
    return null;
  };

  const validatePAN = (pan) => {
    if (!pan || pan.trim() === '') return null;
    const panUpper = pan.toUpperCase();
    if (panUpper.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(panUpper)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return null;
  };

  const validateAadhar = (aadhar) => {
    if (!aadhar || aadhar.trim() === '') return null;
    const aadharDigits = aadhar.replace(/\s/g, '');
    if (aadharDigits.length !== 12) {
      return 'Aadhaar number must be exactly 12 digits';
    }
    if (!/^\d{12}$/.test(aadharDigits)) {
      return 'Aadhaar number must contain only digits';
    }
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('bank.')) {
      const bankField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [bankField]: value
        }
      }));
    } else if (name === 'aadharNumber') {
      const formattedValue = formatAadharNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      
     
      const error = validateAadhar(formattedValue);
      setValidationErrors(prev => ({
        ...prev,
        aadharNumber: error
      }));
    } else if (name === 'name') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        bankDetails: {
          ...prev.bankDetails,
          accountName: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    
    if (name !== 'aadharNumber') {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone: value
    }));

    
    const error = validatePhone(value);
    setValidationErrors(prev => ({
      ...prev,
      phone: error
    }));
  };

  const handlePANChange = (e) => {
    const value = e.target.value.toUpperCase().slice(0, 10);
    setFormData(prev => ({
      ...prev,
      panNumber: value
    }));

   
    const error = validatePAN(value);
    setValidationErrors(prev => ({
      ...prev,
      panNumber: error
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles[0]) {
      const selectedFile = selectedFiles[0];
      
      setFiles(prev => ({
        ...prev,
        [name]: selectedFile
      }));

      if (name === 'profile_image') {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageDataUrl = event.target.result;
          setProfileImagePreview(imageDataUrl);
          
          localStorage.setItem('currentUserProfileImage', imageDataUrl);
          localStorage.setItem('currentUserName', formData.name || 'User');
          
          if (onProfileImageUpdate) {
            onProfileImageUpdate(imageDataUrl, formData.name || 'User');
          }
        };
        reader.readAsDataURL(selectedFile);
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Employee Name is required';
    }
    
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone Number is required';
    } else {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) {
        errors.phone = phoneError;
      }
    }
    
    if (!formData.dob) {
      errors.dob = 'Date of Birth is required';
    }
    
    if (!formData.dateOfJoining) {
      errors.dateOfJoining = 'Date of Joining is required';
    }
    
    if (!formData.panNumber.trim()) {
      errors.panNumber = 'PAN Number is required';
    } else {
      const panError = validatePAN(formData.panNumber);
      if (panError) {
        errors.panNumber = panError;
      }
    }
    
    if (!formData.aadharNumber.trim()) {
      errors.aadharNumber = 'Aadhaar Number is required';
    } else {
      const aadharError = validateAadhar(formData.aadharNumber);
      if (aadharError) {
        errors.aadharNumber = aadharError;
      }
    }
    
    if (!formData.bankDetails.accountName.trim()) {
      errors.accountName = 'Account Name is required';
    }
    
    if (!formData.bankDetails.accountNumber.trim()) {
      errors.accountNumber = 'Account Number is required';
    }
    
    if (!formData.bankDetails.ifscCode.trim()) {
      errors.ifscCode = 'IFSC Code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankDetails.ifscCode.toUpperCase())) {
      errors.ifscCode = 'Invalid IFSC format (e.g., BANK0001234)';
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      alert(firstError);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const submitData = new FormData();
      const cleanPhone = formData.phone.replace(/\s/g, '');
      const cleanAadhar = formData.aadharNumber.replace(/\s/g, '');
 
      submitData.append('name', formData.name.trim());
      submitData.append('role', formData.role);
      submitData.append('email', formData.email.trim());
      submitData.append('phone_number', cleanPhone); 
      submitData.append('dob', formData.dob);
      submitData.append('date_of_joining', formData.dateOfJoining);
      submitData.append('pan_number', formData.panNumber.toUpperCase().trim());
      submitData.append('aadhar_number', cleanAadhar); 
      submitData.append('account_holder_name', formData.bankDetails.accountName.trim());
      submitData.append('bank_account_number', formData.bankDetails.accountNumber.trim());
      submitData.append('ifsc_code', formData.bankDetails.ifscCode.toUpperCase().trim());

      if (formData.pfNumber) {
        submitData.append('pf_number', formData.pfNumber);
      }
      if (formData.esiNumber) {
        submitData.append('esi_number', formData.esiNumber);
      }
      if (formData.uanNumber) {
        submitData.append('uan_number', formData.uanNumber);
      }

      if (files.profile_image) {
        submitData.append('profile_image', files.profile_image);
      }
      if (files.pan_image) {
        submitData.append('pan_image', files.pan_image);
      }
      if (files.aadhar_image) {
        submitData.append('aadhar_image', files.aadhar_image);
      }
      if (files.bank_image) {
        submitData.append('bank_image', files.bank_image);
      }

      const endpoint = `${API_URL}/employees/create_update_employees`;

      const response = await fetch(endpoint, {
        method: 'POST',
        body: submitData,
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.bool && result.data && result.data.length > 0) {
          const latestEmployee = result.data[result.data.length - 1];

          const newEmployee = {
            id: latestEmployee.employee_id,
            name: latestEmployee.name,
            role: latestEmployee.role,
            email: latestEmployee.email,
            phone: latestEmployee.phone_number,
            status: latestEmployee.status || 'Active',
            dob: formData.dob,
            dateOfJoining: formData.dateOfJoining, 
            panNumber: formData.panNumber.toUpperCase().trim(),
            aadharNumber: cleanAadhar,
            pfNumber: formData.pfNumber,
            esiNumber: formData.esiNumber,
            uanNumber: formData.uanNumber,
            bankDetails: {
              accountName: formData.bankDetails.accountName.trim(),
              accountNumber: formData.bankDetails.accountNumber.trim(),
              ifscCode: formData.bankDetails.ifscCode.toUpperCase().trim(),
              branch: formData.bankDetails.branch.trim()
            },
            avatar: profileImagePreview || '👤',
            profileImage: profileImagePreview
          };

          if (onAddEmployee) {
            onAddEmployee(newEmployee);
          }

          if (profileImagePreview) {
            localStorage.setItem('currentUserProfileImage', profileImagePreview);
            localStorage.setItem('currentUserName', formData.name.trim());
            
            if (onProfileImageUpdate) {
              onProfileImageUpdate(profileImagePreview, formData.name.trim());
            }
          }

          alert(result.message || 'Employee added successfully!');
          resetFormAndGoBack();
        } else {
          throw new Error(result.message || 'Failed to create employee');
        }
      } else {
        const errorText = await response.text();
        
        let errorResult;
        try {
          errorResult = JSON.parse(errorText);
        } catch (e) {
          errorResult = { message: `Server error: ${response.status}` };
        }
        
        if (errorResult && errorResult.message) {
          if (errorResult.message.includes('Aadhar number') && errorResult.message.includes('already exists')) {
            alert('This Aadhar number is already registered');
          } else if (errorResult.message.includes('email') && errorResult.message.includes('already exists')) {
            alert('This email is already registered');
          } else if (errorResult.message.includes('PAN number') && errorResult.message.includes('already exists')) {
            alert('This PAN number is already registered');
          } else {
            alert(`Error: ${errorResult.message}`);
          }
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert('Network error: Unable to connect to server. Please check if the backend is running.');
      } else {
        alert(`Error: ${error.message}`);
      }
      
    } finally {
      setLoading(false);
    }
  };

  const resetFormAndGoBack = () => {
    setFormData({
      name: '',
      role: '',
      email: '',
      phone: '',
      dob: '',
      dateOfJoining: '', 
      panNumber: '',
      aadharNumber: '',
      pfNumber: '',
      esiNumber: '',
      uanNumber: '',
      bankDetails: {
        accountName: '',
        accountNumber: '',
        ifscCode: '',
        branch: ''
      }
    });
    
    setFiles({
      profile_image: null,
      pan_image: null,
      aadhar_image: null,
      bank_image: null
    });
    
    setProfileImagePreview(null);
    setValidationErrors({});
    onBack();
  };

  const handleCancel = () => {
    onBack();
  };

  return (
    <div className="ANE-00">
      <div className="ANE-01">
        <h2 className="ANE-02">Add New Employee</h2>

        <form onSubmit={handleSubmit} className="ANE-03">
          <div>
            <label className="ANE-04">Employee Name *</label> 
            <br />
            <input 
              type="text" 
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter Employee Name" 
              className="ANE-05"
              style={validationErrors.name ? { borderColor: 'red' } : {}}
            />
            {validationErrors.name && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.name}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-06">Role *</label> 
            <br />
            <select 
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="ANE-07"
              style={validationErrors.role ? { borderColor: 'red' } : {}}
            >
              <option value="">Select Role</option>
              <option value="Admin">Admin</option>
              <option value="Junior Technician">Junior Technician</option>
              <option value="Helper">Helper</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Technician">Technician</option>
              <option value="Kit Operator">Kit Operator</option>
              <option value="Senior Engineer">Senior Engineer</option>
              <option value="CEO - Software">CEO - Software</option>
              <option value="Site Engineer">Site Engineer</option>
              <option value="Managing Director">Managing Director</option>
              <option value="Assistant Admin">Assistant Admin</option>
              <option value="Full Stack Developer">Full Stack Developer</option>
              <option value="Front-End Developer">Front-End Developer</option>
              <option value="Backend Developer">Backend Developer</option>
              <option value="Manual Testing">Manual Testing</option>
              <option value="CEO-Site Workers">CEO-Site Workers</option>
            </select>
            {validationErrors.role && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.role}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-08">Email *</label> 
            <br />
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter Employee's Email ID" 
              className="ANE-09"
              style={validationErrors.email ? { borderColor: 'red' } : {}}
            />
            {validationErrors.email && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.email}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-10">Phone Number *</label> 
            <br />
            <input 
              type="text" 
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter 10-digit Phone Number" 
              className="ANE-11"
              maxLength="10"
              style={validationErrors.phone ? { borderColor: 'red' } : {}}
            />
            {validationErrors.phone && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.phone}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-04">Date of Birth *</label> 
            <br />
            <input 
              type="date" 
              name="dob"
              value={formData.dob}
              onChange={handleInputChange}
              className="ANE-05"
              style={validationErrors.dob ? { borderColor: 'red' } : {}}
            />
            {validationErrors.dob && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.dob}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-04">Date of Joining *</label> 
            <br />
            <input 
              type="date" 
              name="dateOfJoining"
              value={formData.dateOfJoining}
              onChange={handleInputChange}
              className="ANE-05"
              style={validationErrors.dateOfJoining ? { borderColor: 'red' } : {}}
            />
            {validationErrors.dateOfJoining && (
              <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                {validationErrors.dateOfJoining}
              </span>
            )}
          </div>

          <div>
            <label className="ANE-04">Profile Image</label> 
            <br />
            <input 
              type="file" 
              name="profile_image"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.gif"
              className="ANE-20" 
            />
            {profileImagePreview && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ fontSize: '14px', color: '#666' }}>Preview:</p>
                <img 
                  src={profileImagePreview} 
                  alt="Profile Preview" 
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '2px solid #ddd'
                  }} 
                />
              </div>
            )}
          </div>

          <div className="ANE-12">
            <div className="ANE-13">
              <label className="ANE-14">Pan Number *</label> 
              <br />
              <input 
                type="text" 
                name="panNumber"
                value={formData.panNumber}
                onChange={handlePANChange}
                placeholder="ABCDE1234F (10 characters)" 
                className="ANE-16"
                maxLength="10"
                style={{ 
                  textTransform: 'uppercase',
                  ...(validationErrors.panNumber ? { borderColor: 'red' } : {})
                }}
              />
              {validationErrors.panNumber && (
                <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  {validationErrors.panNumber}
                </span>
              )}
            </div>
          </div>

          <div className="ANE-17">
            <div className="ANE-18">
              <label className="ANE-19">Pan Upload</label> 
              <br />
              <input 
                type="file" 
                name="pan_image"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="ANE-20" 
              />
            </div>
          </div>

          <div className="ANE-21">
            <div className="ANE-22">
              <label className="ANE-23">Aadhar Number *</label> 
              <br />
              <input 
                type="text" 
                name="aadharNumber"
                value={formData.aadharNumber}
                onChange={handleInputChange}
                placeholder="1234 5678 9012 (12 digits)" 
                className="ANE-25"
                maxLength="14"
                style={validationErrors.aadharNumber ? { borderColor: 'red' } : {}}
              />
              {validationErrors.aadharNumber && (
                <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  {validationErrors.aadharNumber}
                </span>
              )}
            </div>
          </div>

          <div className="ANE-26">
            <div className="ANE-27">
              <label className="ANE-28">Aadhar Upload</label> 
              <br />
              <input 
                type="file" 
                name="aadhar_image"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="ANE-29" 
              />
            </div>
          </div>

          <div className="ANE-30">
            <label className="ANE-31">Bank Details</label>  
            <br />
            <div className="ANE-33">
              <div>
                <input 
                  type="text" 
                  name="bank.accountName"
                  value={formData.bankDetails.accountName}
                  onChange={handleInputChange}
                  placeholder="A/C Name *" 
                  className="ANE-34"
                  style={validationErrors.accountName ? { borderColor: 'red' } : {}}
                />
                {validationErrors.accountName && (
                  <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    {validationErrors.accountName}
                  </span>
                )}
              </div>
              <div>
                <input 
                  type="text" 
                  name="bank.accountNumber"
                  value={formData.bankDetails.accountNumber}
                  onChange={handleInputChange}
                  placeholder="A/C Number *" 
                  className="ANE-35"
                  style={validationErrors.accountNumber ? { borderColor: 'red' } : {}}
                />
                {validationErrors.accountNumber && (
                  <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    {validationErrors.accountNumber}
                  </span>
                )}
              </div>
              <div>
                <input 
                  type="text" 
                  name="bank.ifscCode"
                  value={formData.bankDetails.ifscCode}
                  onChange={handleInputChange}
                  placeholder="IFSC Code (e.g., BANK0001234) *" 
                  className="ANE-36"
                  style={{ 
                    textTransform: 'uppercase',
                    ...(validationErrors.ifscCode ? { borderColor: 'red' } : {})
                  }}
                />
                {validationErrors.ifscCode && (
                  <span style={{ color: 'red', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    {validationErrors.ifscCode}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="ANE-38">
            <label className="ANE-39">Bank Upload</label>
            <input 
              type="file" 
              name="bank_image"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              className="ANE-41" 
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div>
                <label className="ANE-04">PF No</label> 
                <br />
                <input 
                  type="text" 
                  name="pfNumber"
                  value={formData.pfNumber}
                  onChange={handleInputChange}
                  placeholder="Enter PF Number" 
                  className="ANE-05"
                />
              </div>

              <div>
                <label className="ANE-04">ESI No</label> 
                <br />
                <input 
                  type="text" 
                  name="esiNumber"
                  value={formData.esiNumber}
                  onChange={handleInputChange}
                  placeholder="Enter ESI Number" 
                  className="ANE-05"
                />
              </div>

              <div>
                <label className="ANE-04">UAN No</label> 
                <br />
                <input 
                  type="text" 
                  name="uanNumber"
                  value={formData.uanNumber}
                  onChange={handleInputChange}
                  placeholder="Enter UAN Number" 
                  className="ANE-05"
                />
              </div>
            </div>
          </div>

          <div className="ANE-42">
            <button 
              type="submit" 
              className="ANE-43"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button 
              type="button" 
              className="ANE-44"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewEmployees;